import React from 'react';
import RunSheetDriverList from './RunSheetDriverUpdateList';
import RunSheetDriverCallout from './RunSheetDriverUpdateCallout';
import RunSheetDriverPlan from './RunSheetDriverUpdatePlanChanges';
import RunSheetDriverOffice from './RunsheetDriverUpdateOffice';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CheckPermissions, GetQuery, PostQuery } from './Shared';
import type { RunsheetDriverJob } from './models/JobScheduleInterfaces';
import type { ItemOptionList } from './models/JobSharedInterfaces';
import type { ExtraInvoiceItems, ItemTypeList } from './models/InvoiceInterfaces';

const RunSheetDriverUpdateData = () => {
    let navigate = useNavigate();
    const [jobs, setJobs] = React.useState<RunsheetDriverJob[]>([]);
    const [tab, setTab] = React.useState("list");
    const [invoiceItemTypes, setInvoiceItemTypes] = React.useState<ItemTypeList[]>([]);
    const [itemOptions, setItemOptions] = React.useState<ItemOptionList[]>([]);
    const [gst, setGst] = React.useState(0.1);
    //const [jobIndex, setJobIndex] = React.useState(0);
    const initJob: RunsheetDriverJob = {
        runsheetId: 0,
        scheduleBranchId: 1,
        scheduleDate: "",
        scheduleDateFormatted: "",
        onHireDate: "",
        truck: "",
        truckId: -1,
        jobId: 0,
        siteAddress: "",
        customerName: "",
        customerAccountType: "",
        updateTypeId: 1,
        estimatedTime: "",
        departureTime: "",
        siteArrivalTime: "",
        downTime: "",
        siteCompletionTime: "",
        driverNotes: "",
        noCalloutInvoiceNotes: "",
        calloutNotes: "",
        planChangesReason: "",
        officeNotes: "",
        truckHireRate: 0,
        items: [],
        invoiceItems: []
    };
    const [job, setJob] = React.useState<RunsheetDriverJob>(initJob);
    const [systemCompanyId, setSystemCompanyId] = React.useState(2);
    const [loading, setLoading] = React.useState(true);
        
    React.useEffect(() => {
        window.scrollTo(0, 0);
        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("ViewRunSheetUpdates");
        if (hasPermission) {
            getInvoiceTypes();
        } else {
            navigate("/accessdenied");
        }
    }

    const getInvoiceTypes = async () => {
        const apiUrl = "GetItems";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setItemOptions(result.data);
        }

        const apiUrlTypes = "GetInvoiceItemTypes";
        var result = await GetQuery(apiUrlTypes);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setInvoiceItemTypes(result.data.itemTypes);
            setGst(result.data.gst);
            getRunsheetDriverJobs();
        }
    }

    const getRunsheetDriverJobs = async () => {
        const apiUrl = "GetRunsheetDriverJobs";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setJobs(result.data.jobs);
            setSystemCompanyId(result.data.systemCompanyId);
            setLoading(false);
        }
    }

    const resetJob = () => {
        var job: RunsheetDriverJob = {
            runsheetId: 0,
            scheduleBranchId: 1,
            scheduleDate: "",
            scheduleDateFormatted: "",
            onHireDate: "",
            truck: "",
            truckId: -1,
            jobId: 0,
            siteAddress: "",
            customerName: "",
            customerAccountType: "",
            updateTypeId: 1,
            estimatedTime: "",
            departureTime: "",
            siteArrivalTime: "",
            downTime: "",
            siteCompletionTime: "",
            driverNotes: "",
            noCalloutInvoiceNotes: "",
            calloutNotes: "",
            planChangesReason: "",
            officeNotes: "",
            truckHireRate: 0,
            items: [],
            invoiceItems: []
        };

        return job;
    }

    const updateJobField = (fieldName: string, fieldValue: string) => {
        setJob(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const updateJobItemFieldNumber = (itemIndex: number, fieldName: string, fieldValue: number) => {
        //update item that has changed
        var items = [...job.items];
        var item = items[itemIndex];
        let newItem: any = {};
        newItem = item;
        newItem[fieldName] = fieldValue;
        setJob(prevState => ({ ...prevState, items: items }));
    }

    const updateJobKsFieldNumber = (itemIndex: number, ksItemIndex: number, fieldName: string, fieldValue: number) => {
        //update item that has changed
        var items = [...job.items];
        var item = items[itemIndex];
        var ksItem = item.kwikstageItems[ksItemIndex];
        let newItem: any = {};
        newItem = ksItem;
        newItem[fieldName] = fieldValue;
        setJob(prevState => ({ ...prevState, items: items }));
    }

    const updateJobInvoices = (updatedInvoiceItems: ExtraInvoiceItems[]) => {
        setJob(prevState => ({ ...prevState, invoiceItems: updatedInvoiceItems }));
    }

    //const updateJob = (updatedJob: RunsheetDriverJob) => {
    //    setJob(updatedJob);
    //}

    //const gotoList = () => {
    //    var updatedJob = resetJob();

    //    setTab("list");
    //    setJobIndex(0);
    //    setJob(updatedJob);
    //}

    const gotoCallout = (updatedJobIndex: number) => {
        var allJobs = [...jobs];
        var jobToUpdate = allJobs[updatedJobIndex];

        //create a blank callout row
        var calloutInvItems: ExtraInvoiceItems[] = [];
        var calloutInvItem: ExtraInvoiceItems = {
            rowIndex: 0,
            id: job.runsheetId,
            itemId: -1,
            itemTypeId: -1,
            scheduleId: 0,
            jobQuoteItemId: 0,
            rate: 0,
            gst: 0,
            amount: 0,
            description: "",
            active: true
        };
        calloutInvItems.push(calloutInvItem);
        jobToUpdate.invoiceItems = calloutInvItems;

        setTab("callout");
        //setJobIndex(updatedJobIndex);
        setJob(jobToUpdate);
    }

    const saveCallout = () => {
        saveCalloutItem();
    }

    const saveCalloutItem = async () => {
        var jobInvItems = [...job.invoiceItems.filter(ii => ii.active)];
        jobInvItems.pop();

        var callout = {
            scheduleDate: job.scheduleDate,
            runsheetId: job.runsheetId,
            jobId: job.jobId,
            truck: job.truck,
            departureTime: job.departureTime,
            siteArrivalTime: job.siteArrivalTime,
            downTime: job.downTime,
            siteCompletionTime: job.siteCompletionTime,
            calloutNotes: job.calloutNotes,
            noCalloutInvoiceNotes: job.noCalloutInvoiceNotes,
            extraInvoiceItems: jobInvItems,
        };

        //save callout invoice
        var jsonItem = JSON.stringify(callout);

        const apiUrl = "SaveRunsheetDriverCallout";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            toast.success("Callout Invoice Saved");

            var resetJobData = resetJob();
            setJob(resetJobData);
            setTab("list");
            //setJobIndex(0);

            //get updated list
            getRunsheetDriverJobs();
        }
    }

    const gotoPlanChanges = (updatedJobIndex: number) => {
        var allJobs = [...jobs];
        var jobToUpdate = allJobs[updatedJobIndex];

        setTab("plan");
        //setJobIndex(updatedJobIndex);
        setJob(jobToUpdate);
    }

    const savePlanChanges = () => {
        var resetJobData = resetJob();
        setJob(resetJobData);
        setTab("list");
        //setJobIndex(0);

        //get updated list
        getRunsheetDriverJobs();
    }

    const gotoOfficeUpdate = (updatedJobIndex: number) => {
        var allJobs = [...jobs];
        var jobToUpdate = allJobs[updatedJobIndex];

        setTab("office");
        //setJobIndex(updatedJobIndex);
        setJob(jobToUpdate);
    }

    const saveOfficeUpdates = () => {
        saveOfficeUpdateItem();
    }

    const saveOfficeUpdateItem = async () => {
        var officeUpdate = {
            scheduleDate: job.scheduleDate,
            runsheetId: job.runsheetId,
            jobId: job.jobId,
            truck: job.truck,
            departureTime: job.departureTime,
            siteArrivalTime: job.siteArrivalTime,
            downTime: job.downTime,
            siteCompletionTime: job.siteCompletionTime,
            officeNotes: job.officeNotes,
            items: job.items
        };

        var jsonItem = JSON.stringify(officeUpdate);

        const apiUrl = "SaveRunsheetOfficeUpdate";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            toast.success("Office Changes Saved");

            var resetJobData = resetJob();
            setJob(resetJobData);
            setTab("list");
            //setJobIndex(0);

            //get updated list
            getRunsheetDriverJobs();
        }
    }

    var subcontents;
    if (tab == "list") {
        subcontents = <RunSheetDriverList jobs={jobs} gotoCallout={gotoCallout} gotoPlanChanges={gotoPlanChanges} gotoOfficeUpdate={gotoOfficeUpdate} />
    }
    else if (tab == "callout") {
        subcontents = <RunSheetDriverCallout invoiceItemTypes={invoiceItemTypes} gst={gst} job={job} updateJobField={updateJobField} updateJobInvoices={updateJobInvoices} save={saveCallout} />
    }
    else if (tab == "plan") {
        subcontents = <RunSheetDriverPlan systemCompanyId={systemCompanyId} runsheetId={job.runsheetId} jobId={job.jobId} itemOptions={itemOptions} save={savePlanChanges} />
    }
    else if (tab == "office") {
        subcontents = <RunSheetDriverOffice job={job} updateJobField={updateJobField} updateJobItemFieldNumber={updateJobItemFieldNumber} updateJobKsFieldNumber={updateJobKsFieldNumber} save={saveOfficeUpdates} />
    }

    let contents = loading
        ? <p><em>Loading...</em></p>
        : subcontents;

    return (
        <div>
            {contents}
        </div>
    );

};

export default RunSheetDriverUpdateData;
